import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import apiService from '../services/api';

const Login = ({ setIsAuthenticated }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resetSuccess = location.state?.resetSuccess || searchParams.get('reset') === '1';
  const activated = searchParams.get('activated') === '1' || location.state?.activated;
  const prefillEmail = searchParams.get('email') || location.state?.email || '';

  const [formData, setFormData] = useState({
    email: prefillEmail,
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Si les tokens ont été purgés (flow invitation), resynchroniser l'état React.
  useEffect(() => {
    const token = localStorage.getItem('admin_access_token') || localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');
    if (!token || !userData) {
      setIsAuthenticated(false);
    }
  }, [setIsAuthenticated]);

  const successMessage = useMemo(() => {
    if (activated) return 'Votre compte est activé. Connectez-vous avec votre email et votre nouveau mot de passe.';
    if (resetSuccess) return 'Mot de passe mis à jour. Vous pouvez vous connecter.';
    return null;
  }, [activated, resetSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await apiService.adminLogin(formData.email, formData.password);
      
      if (data.success) {
        let user = data.user || {};

        // Si COMMERCANT sans commercantId ou roleInterne, hydrater depuis /auth/profile
        if (user.role === 'COMMERCANT' && (!user.commercantId || !user.roleInterne)) {
          try {
            const profileData = await apiService.verifyToken();
            const profileUser = profileData?.user;
            if (profileUser) {
              user = { ...user, ...profileUser };
              localStorage.setItem('admin_user', JSON.stringify(user));
            }
          } catch { /* best effort */ }
        }

        // Signaler que le user est prêt AVANT de monter le Layout.
        // Une seule redirection : App remplace /login par <Navigate /> (évite double navigate).
        window.dispatchEvent(new Event('authUserUpdated'));
        setIsAuthenticated(true);
      } else {
        setError(data.error || 'Email ou mot de passe incorrect');
      }
    } catch (err) {
      setError(err.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <Lock className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Administration Clickorico
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Connectez-vous à votre espace d'administration
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {successMessage}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="input-field pl-10"
                  placeholder="admin@clickorico.fr"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  autoComplete="username"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="input-field pl-10 pr-10"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-right">
                <Link to="/mot-de-passe-oublie" className="text-sm text-primary-600 hover:underline">
                  Mot de passe oublié ?
                </Link>
              </p>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Se connecter'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
